import {
  HTTP_STATUS_CODES,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import nanoid from 'nanoid';

import { GenerateAIQuestionsEventAdapter } from '~analytics/adapters/GenerateAIQuestionsEventAdapter';

import localAxios from '../localAxios';
import { generateQuestionURL } from './urls';

interface GenerateQuestionParams {
  questionCount: number;
  topic?: string;
  includeExplanation: boolean;
  questionTypes: Array<keyof typeof QuestionTypes>;
  fileKey?: string;
  fileText?: string;
  fileMetadata?: { type: string; size: number };
}

export interface AnswerResponse {
  order: number;
  text: string;
  isCorrect: boolean;
}

export interface QuestionResponse {
  questionType: QuestionTypes;
  questionText: string;
  explanation: string;
  answers: AnswerResponse[];
}

export interface GenerateQuestionResponse {
  questions: QuestionResponse[];
  elapsedTime: number;
  promptVersion: string;
  version: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface AsyncGenerateQuestionResponse {
  async: true;
  requestId: string;
  fileKey: string;
  message: string;
  elapsedTime: number;
}

export async function generateQuestions({
  questionCount,
  topic,
  questionTypes,
  includeExplanation,
  fileKey,
  fileText,
  fileMetadata,
}: GenerateQuestionParams) {
  const questionURL = generateQuestionURL();

  const { data, status } = await localAxios.post<
    GenerateQuestionResponse | AsyncGenerateQuestionResponse
  >(questionURL.href, {
    questionCount,
    questionTypes,
    includeExplanation,
    ...(topic && { topic }),
    ...(fileKey && { fileKey }),
    ...(fileText && { fileText }),
  });

  // Check if async response (file-based generation)
  if (
    'async' in data &&
    data.async &&
    status === HTTP_STATUS_CODES.HTTP_202_ACCEPTED
  ) {
    return {
      async: true,
      requestId: data.requestId,
      fileKey: data.fileKey,
      elapsedTime: data.elapsedTime,
    };
  }

  const { questions, promptVersion, version, elapsedTime, usage } =
    data as GenerateQuestionResponse;

  // Calculate file upload analytics
  const fileUploaded = !!fileKey;
  const fileUploadType = fileMetadata?.type || null;
  const fileUploadSizeBytes = fileMetadata?.size || null;
  const filesUploadedCount = fileKey ? 1 : 0;

  // Analytics
  new GenerateAIQuestionsEventAdapter({
    numQuestions: questionCount,
    multipleChoice: questionTypes.includes('MultipleChoice'),
    trueFalse: questionTypes.includes('TrueFalse'),
    shortAnswer: questionTypes.includes('FreeResponse'),
    includeExplanation: includeExplanation,
    promptLength: topic.length,
    promptVersion,
    fileUploaded,
    fileUploadType,
    fileUploadSizeBytes,
    filesUploadedCount,
    version: version,
    responseMilliseconds: elapsedTime,
    ...usage,
  }).sendAnalytics();

  return {
    questions: questions.map((question) => ({
      questionId: nanoid(),
      ...question,
    })),
    elapsedTime: elapsedTime,
    version: version,
    promptVersion: promptVersion,
  };
}
